import { useQuery, useLazyQuery, useMutation } from "@apollo/client/react";
import { useState, useMemo, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import toast from 'react-hot-toast';
import { Puntaje } from '../components/TabDatosSeleccion/Puntaje';
import { Contrato } from '../components/TabDatosSeleccion/Contrato';
import { Vacunacion } from '../components/TabDatosSeleccion/Vacunacion';
import { Curriculum } from '../components/TabDatosSeleccion/Curriculum';
import { obtenerDataImagen } from '../../../../actions/ImagenAction';
import { FirmaProfesional } from '../components/TabDatosSeleccion/FirmaProfesional';
import { SeguroMalaPraxis } from '../components/TabDatosSeleccion/SeguroMalaPraxis';
import { adaptResultadoSeleccionArray, GET_RESULTADOS_SELECCION } from '../../../../data/graphQL/queries/Profesionales/getResultadosSeleccion';
import DocumentViewer from '../../../../components/DocumentViewer';
import { DecodeBase64ToBytesArray } from "../../../../helpers/tools";
import { adaptSegurosMalaPraxis, GET_ARCHIVOS_PROFESIONAL } from '../../../../data/graphQL/queries/Profesionales/getArchivosProfesional';
import { ADD_DOCUMENTO_PROFESIONAL, DELETE_DOCUMENTOS_PROFESIONAL } from "../../../../data/graphQL/mutations/Profesionales/documentosProfesional";

// Mapa de campo -> idTipoDocumento
const TIPO_DOCUMENTO_MAP = {
  tituloHabilitante: 28,
  matricula: 29,
  certificadoVacunacion: 30,
  seguroMalaPraxis: 31,
  curriculum: 32,
  contrato: 33,
  firmaDigital: 34
};

export const TabDatosSeleccion = ({
  allowedWrite,
  isNew,
  profesionalLoading,
  delegacionFormData,
  datosGeneralesFormData,
  datosSeleccionFormData,
  setDatosSeleccionFormData,
  esEnfermeroCuidador,
  esAsistenteSocial,
  idProfesional,
  esRehabilitador
}) => {
  const [resultadoSeleccionOption, setResultadoSeleccionOption] = useState([]);
  const [openPreview, setOpenPreview] = useState(false);
  const [viewDocumentData, setViewDocumentData] = useState({});

  const { data: resultadosSeleccion, loading: resultadoSeleccionLoading } = useQuery(GET_RESULTADOS_SELECCION);

  const [getArchivosProfesional, { data: profesionalAdjuntos, loading: archivosLoading }] = useLazyQuery(GET_ARCHIVOS_PROFESIONAL, {
    fetchPolicy: 'network-only'
  });

  // Mutations
  const [addDocumentoProfesional, { loading: addingDocumento }] = useMutation(ADD_DOCUMENTO_PROFESIONAL);
  const [deleteDocumentosProfesional, { loading: deletingDocumento }] = useMutation(DELETE_DOCUMENTOS_PROFESIONAL);

  useEffect(() => {
    if (!profesionalAdjuntos) return;
    setDatosSeleccionFormData((prevData) => ({
      ...prevData,
      curriculumCheck: !!profesionalAdjuntos?.profesionalAdjuntos?.curriculum?.length,
      curriculum: profesionalAdjuntos?.profesionalAdjuntos.curriculum,
      contratoCheck: !!profesionalAdjuntos?.profesionalAdjuntos?.contrato?.length,
      contrato: profesionalAdjuntos?.profesionalAdjuntos.contrato,
      certificadoVacunacionCheck: !!profesionalAdjuntos?.profesionalAdjuntos?.certificadoVacunacion?.length,
      certificadoVacunacion: profesionalAdjuntos?.profesionalAdjuntos.certificadoVacunacion,
      seguroMalaPraxis: adaptSegurosMalaPraxis(profesionalAdjuntos?.profesionalAdjuntos.seguroMalaPraxis || []),
      tituloHabilitanteCheck: !!profesionalAdjuntos?.profesionalAdjuntos?.tituloHabilitante?.length,
      tituloHabilitante: profesionalAdjuntos?.profesionalAdjuntos.tituloHabilitante,
      firmaPersonalCheck: !!profesionalAdjuntos?.profesionalAdjuntos?.firmaDigital,
      firmaDigital: profesionalAdjuntos?.profesionalAdjuntos.firmaDigital,
      matriculaCheck: !!profesionalAdjuntos?.profesionalAdjuntos?.matricula?.length,
      matricula: profesionalAdjuntos?.profesionalAdjuntos.matricula
    }));
  }, [profesionalAdjuntos]);

  useEffect(() => {
    if (!idProfesional) return;
    getArchivosProfesional({ variables: { idProfesional } });
  }, [idProfesional]);

  useEffect(() => {
    if (!resultadosSeleccion) return;
    setResultadoSeleccionOption(adaptResultadoSeleccionArray(resultadosSeleccion));
  }, [resultadosSeleccion]);

  const handleChange = (name, value) => {
    setDatosSeleccionFormData((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleChangeArchivo = async (name, value) => {
    if (!value) return;

    const idTipoDocumento = TIPO_DOCUMENTO_MAP[name];

    if (!idTipoDocumento) {
      console.error('Tipo de documento no encontrado para:', name);
      return;
    }
    const image = await obtenerDataImagen(value);

    try {
      const { data } = await addDocumentoProfesional({
        variables: {
          input: {
            archivo: image?.data,
            contentType: image?.tipo,
            titulo: image?.nombre,
            extension: image?.extension
          },
          idProfesional,
          idTipoDocumento
        }
      });

      if (data?.addDocumentoProfesional?.success) {
        toast.success('Documento adjuntado correctamente');
        getArchivosProfesional({ variables: { idProfesional } });
      } else {
        toast.error(data?.addDocumentoProfesional?.message || 'Error al adjuntar documento');
      }
    } catch (error) {
      console.error('Error al subir documento:', error);
      toast.error('Error al adjuntar documento');
    }
  };

  const handleDelete = async (name) => {
    const idTipoDocumento = TIPO_DOCUMENTO_MAP[name];

    if (!idTipoDocumento) {
      console.error('Tipo de documento no encontrado para:', name);
      return;
    }

    try {
      const { data } = await deleteDocumentosProfesional({
        variables: {
          idTipoDocumento,
          idProfesional
        }
      });

      if (data?.deleteDocumentosProfesional?.success) {
        toast.success('Documento eliminado correctamente');
        setDatosSeleccionFormData((prev) => ({
          ...prev,
          [name]: null
        }));
      } else {
        toast.error(data?.deleteDocumentosProfesional?.message || 'Error al eliminar documento');
      }
    } catch (error) {
      console.error('Error al eliminar documento:', error);
      toast.error('Error al eliminar documento');
    }
  };

  const handlePreview = (field) => {
    if (!!datosSeleccionFormData[field]?.titulo) {
      setOpenPreview(true);
      setViewDocumentData({
        ...datosSeleccionFormData[field],
        archivo: DecodeBase64ToBytesArray(datosSeleccionFormData[field].archivo)
      });
    }
  };

  const handleClosePreview = () => {
    setOpenPreview(false);
    setViewDocumentData({});
  };

  const loadingPage = useMemo(() => {
    return profesionalLoading || resultadoSeleccionLoading;
  }, [profesionalLoading, resultadoSeleccionLoading]);

  const isProcessingDocumento = addingDocumento || deletingDocumento;

  if (loadingPage || archivosLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '20vh' }}>
      <CircularProgress size={40} />
    </Box>
  );

  return (
    <>
      <Puntaje
        datosSeleccionFormData={datosSeleccionFormData}
        resultadosSeleccion={resultadoSeleccionOption}
        handleChange={handleChange}
        handlePreview={handlePreview}
        handleChangeArchivo={handleChangeArchivo}
        handleDelete={handleDelete}
        allowedWrite={allowedWrite && !isProcessingDocumento}
        isNew={isNew}
        esEnfermeroCuidador={esEnfermeroCuidador}
        esRehabilitador={esRehabilitador}
        idTipoProfesional={datosGeneralesFormData?.idTipoProfesional}
      />

      <Vacunacion
        datosSeleccionFormData={datosSeleccionFormData}
        handleChange={handleChange}
        handlePreview={handlePreview}
        handleChangeArchivo={handleChangeArchivo}
        handleDelete={handleDelete}
        allowedWrite={allowedWrite && !isProcessingDocumento}
        isNew={isNew}
      />

      <Curriculum
        datosSeleccionFormData={datosSeleccionFormData}
        handleChange={handleChange}
        handlePreview={handlePreview}
        handleChangeArchivo={handleChangeArchivo}
        handleDelete={handleDelete}
        allowedWrite={allowedWrite && !isProcessingDocumento}
        isNew={isNew}
        delegaciones={delegacionFormData?.delegaciones}
      />

      <Contrato
        datosSeleccionFormData={datosSeleccionFormData}
        handleChange={handleChange}
        handlePreview={handlePreview}
        handleChangeArchivo={handleChangeArchivo}
        handleDelete={handleDelete}
        allowedWrite={allowedWrite && !isProcessingDocumento}
        isNew={isNew}
      />

      <FirmaProfesional
        datosSeleccionFormData={datosSeleccionFormData}
        handleChange={handleChange}
        handlePreview={handlePreview}
        handleChangeArchivo={handleChangeArchivo}
        handleDelete={handleDelete}
        allowedWrite={allowedWrite && !isProcessingDocumento}
        isNew={isNew}
      />
      <SeguroMalaPraxis
        datosSeleccionFormData={datosSeleccionFormData}
        handleChange={handleChange}
        allowedWrite={allowedWrite && !isProcessingDocumento}
        isNew={isNew}
        setOpenPreview={setOpenPreview}
        setViewDocumentData={setViewDocumentData}
        handleClosePreview={handleClosePreview}
        idProfesional={idProfesional}
        refetchArchivos={() => getArchivosProfesional({ variables: { idProfesional } })}
      />

      {openPreview && (
        <DocumentViewer
          data={viewDocumentData}
          setOpenDlg={() => handleClosePreview()}
          download={true}
        />
      )}
    </>
  );
};